import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { tenantOverride, useTenantMutations, useTenants } from '../api/admin'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal, fmtDateTime } from '../components/ui'
import { errMsg } from './TemplatesPage'

export default function TenantsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isSuper = user?.roles.some((r) => r.role === 'SuperAdmin') ?? false
  const tenants = useTenants(isSuper)
  const m = useTenantMutations()
  const [draft, setDraft] = useState<{ name: string; slug: string; adminEmail: string; adminFullName: string; adminPassword: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const current = tenantOverride.get()

  if (!isSuper) return <p className="error">{t('tenants.superOnly')}</p>

  const switchTo = (id: string | null) => { tenantOverride.set(id); window.location.href = '/' }
  // A freshly created tenant is what the admin wants to work in next (its users, its quota) — not the previous one.
  const switchToUsers = (id: string) => { tenantOverride.set(id); window.location.href = '/users' }

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('tenants.title')}</h2>
        <button onClick={() => { setError(null); setDraft({ name: '', slug: '', adminEmail: '', adminFullName: '', adminPassword: '' }) }}>{t('tenants.new')}</button>
      </div>
      <p className="muted small">{t('tenants.hint')}</p>
      {current ? <div className="card row between"><span>{t('tenants.actingAs', { name: tenants.data?.items.find((x) => x.id === current)?.name ?? current })}</span><button className="secondary small-btn" onClick={() => switchTo(null)}>{t('tenants.exit')}</button></div> : null}
      <div className="card">
        {tenants.isLoading ? <p>{t('common.loading')}</p> : !tenants.data?.items.length ? <Empty text={t('tenants.empty')} /> : (
          <div className="table-wrap"><table className="table">
            <thead><tr><th>{t('tenants.name')}</th><th>{t('tenants.slug')}</th><th className="num">{t('tenants.users')}</th><th className="num">{t('tenants.quota')}</th><th className="num">{t('tenants.packs')}</th><th>{t('reports.date')}</th><th>{t('users.status')}</th><th /></tr></thead>
            <tbody>{tenants.data.items.map((x) => (
              <tr key={x.id}>
                <td><b>{x.name}</b>{x.id === current ? <Badge color="#2a78d6">{t('tenants.current')}</Badge> : null}</td>
                <td className="small">{x.slug}</td>
                <td className="num">{x.userCount}</td>
                <td className="num" style={{ color: x.usedUsers >= x.maxUsers ? '#e34948' : undefined }}>{x.usedUsers} / {x.maxUsers}</td>
                <td className="num"><input type="number" min={0} max={1000} defaultValue={x.extraUserPacks} style={{ width: 70 }} onBlur={(e) => { const v = Number(e.target.value); if (v !== x.extraUserPacks) { setError(null); m.setPacks.mutateAsync({ id: x.id, packs: v }).catch((err: unknown) => setError(errMsg(err))) } }} title={t('tenants.packsHint')} /></td>
                <td>{fmtDateTime(x.createdAt)}</td>
                <td>{x.isActive ? <Badge color="#008300">{t('users.active')}</Badge> : <Badge color="#e34948">{t('users.inactive')}</Badge>}</td>
                <td className="row" style={{ whiteSpace: 'nowrap' }}>
                  <button className="ghost" onClick={() => switchTo(x.id)}>{t('tenants.switch')}</button>
                  <Link className="button ghost" to={`/tenants/${x.id}/report-files`}>{t('tenants.reportFiles')}</Link>
                  <button className="ghost" onClick={() => { setError(null); m.setActive.mutateAsync({ id: x.id, isActive: !x.isActive }).catch((e: unknown) => setError(errMsg(e))) }}>{x.isActive ? t('tenants.deactivate') : t('tenants.activate')}</button>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {error ? <p className="error">{error}</p> : null}
      </div>

      {draft ? (
        <Modal title={t('tenants.new')} onClose={() => setDraft(null)}>
          <label>{t('tenants.name')}<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: draft.slug || slugify(e.target.value) })} /></label>
          <label>{t('tenants.slug')}<input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} /></label>
          <h3>{t('tenants.admin')}</h3>
          <label>{t('users.name')}<input value={draft.adminFullName} onChange={(e) => setDraft({ ...draft, adminFullName: e.target.value })} /></label>
          <label>{t('users.email')}<input type="text" autoCapitalize="none" value={draft.adminEmail} onChange={(e) => setDraft({ ...draft, adminEmail: e.target.value })} /></label>
          <label>{t('users.password')}<input type="password" value={draft.adminPassword} onChange={(e) => setDraft({ ...draft, adminPassword: e.target.value })} /></label>
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setDraft(null)}>{t('common.cancel')}</button>
            <button disabled={!draft.name || !draft.slug || !draft.adminEmail || !draft.adminFullName || draft.adminPassword.length < 8} onClick={() => { setError(null); m.create.mutateAsync(draft).then((created) => { setDraft(null); switchToUsers(created.id) }).catch((e: unknown) => setError(errMsg(e))) }}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

const slugify = (s: string) => s.toLowerCase().replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
